/**
 * Client-tool declaration serialization (ADR-096).
 *
 * A tool that carries both a `description` and an `inputSchema` is
 * host-invented: its zod schema becomes the model-facing JSON Schema on the
 * chat body (`hostTools`). That pair is the whole opt-in — server-declared
 * executors (Office tools, built-ins) supply neither, because their schema
 * is owned by a Magnus capability. There is no name prefix to remember: the
 * author writes a plain snake_case name (`navigate_in_app`) and the server
 * namespaces it to `{appSurface}__{name}` before the model sees it.
 *
 * Conversion is strict on purpose: an unsupported zod construct throws
 * here, in the host's build, rather than 400-ing every chat send.
 */

import {
  checkHostToolSchema,
  type HostDeclaredTool,
  type HostToolInputSchema,
} from "@magnus/contract/host-tools";
import { HOST_TOOL_NAME_RE } from "@magnus/contract/values";
import { z } from "zod";
import type { AnyMagnusClientTool } from "./types";

/**
 * A host-invented tool the SDK must declare to Magnus: it carries its own
 * `description` and `inputSchema`. Server-declared executors carry neither
 * (their schema lives in a Magnus capability), so they are never declared.
 */
export function isHostDeclaredTool(tool: AnyMagnusClientTool): boolean {
  return Boolean(tool.description && tool.inputSchema);
}

function fail(toolName: string, what: string, fix: string): never {
  throw new TypeError(`defineClientTool("${toolName}"): ${what} — ${fix}`);
}

/** Drop zod's `$schema` and force `additionalProperties: false` on objects —
 *  the only two benign differences between zod's emitter and the wire subset. */
function normalize(node: unknown): unknown {
  if (typeof node !== "object" || node === null || Array.isArray(node)) {
    return node;
  }
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(node)) {
    if (key === "$schema") {
      continue;
    }
    out[key] = value;
  }
  if (out.type === "object") {
    out.additionalProperties = false;
    if (
      typeof out.properties === "object" &&
      out.properties !== null &&
      !Array.isArray(out.properties)
    ) {
      out.properties = Object.fromEntries(
        Object.entries(out.properties).map(([prop, child]) => [
          prop,
          normalize(child),
        ])
      );
    }
  }
  if (out.type === "array") {
    out.items = normalize(out.items);
  }
  return out;
}

/**
 * Convert one host-invented tool to its wire declaration (plain name — the
 * server applies the surface namespace). Throws a `TypeError` naming the
 * tool for every authoring mistake, so the failure lands in the host's
 * build — the bug that motivated ADR-096 was a silent failure three layers
 * downstream.
 */
export function toHostDeclaredTool(
  tool: AnyMagnusClientTool
): HostDeclaredTool {
  if (!HOST_TOOL_NAME_RE.test(tool.name)) {
    fail(
      tool.name,
      "invalid host-declared tool name",
      `names must match ${HOST_TOOL_NAME_RE} (plain snake_case, no double underscores — the server adds the surface namespace)`
    );
  }
  if (!tool.description) {
    fail(
      tool.name,
      "missing description",
      "host-declared tools are sent to the model, which needs a description"
    );
  }
  if (!tool.inputSchema) {
    fail(
      tool.name,
      "missing inputSchema",
      "host-declared tools are sent to the model, which needs the schema; use z.object({}) for a no-input tool"
    );
  }
  const emitted = z.toJSONSchema(tool.inputSchema, {
    io: "input",
    target: "draft-7",
  });
  const schema = normalize(emitted);
  const check = checkHostToolSchema(schema);
  if (!check.ok) {
    fail(
      tool.name,
      `inputSchema is outside the host-declared subset (${check.reason})`,
      "use plain objects/strings/numbers/booleans/enums/arrays without refs, unions, patterns, formats, or defaults"
    );
  }
  return {
    description: tool.description,
    inputSchema: schema as HostToolInputSchema,
    name: tool.name,
  };
}

/** Every host-invented tool (description + inputSchema present) in
 *  registration order, converted to its wire declaration. */
export function serializeDeclaredTools(
  tools: readonly AnyMagnusClientTool[]
): HostDeclaredTool[] {
  const declared = tools.filter(isHostDeclaredTool);
  const seen = new Set<string>();
  for (const tool of declared) {
    if (seen.has(tool.name)) {
      throw new TypeError(
        `defineClientTool("${tool.name}"): duplicate host-declared tool name`
      );
    }
    seen.add(tool.name);
  }
  return declared.map(toHostDeclaredTool);
}
